/**
 * AreaChart - Filled time series visualization
 *
 * Usage:
 *   <AreaChart
 *     title="Revenue over time"
 *     data={{ series: [{ date: '2024-01-01', value: 1200 }, ...] }}
 *   />
 */

import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps, LineChartData } from './types';
import { CHART_COLORS, getSeriesColor } from './colors';
import './blocks.css';

export interface AreaChartProps extends BlockProps<LineChartData> {
  /** Chart height in pixels */
  height?: number;
  /** Stacked areas */
  stacked?: boolean;
  /** Gradient fill */
  gradient?: boolean;
}

export function AreaChart({
  data,
  title,
  config,
  height = 250,
  stacked = false,
  gradient = true,
  className,
}: AreaChartProps) {
  const { series, multiSeries, seriesNames } = data;

  // Normalize data
  const chartData = multiSeries || series?.map(point => ({
    date: point.date,
    value: point.value,
  }));

  if (!chartData || chartData.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const keysToPlot = seriesNames || (series ? ['value'] : Object.keys(chartData[0]).filter(k => k !== 'date'));

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: 'var(--color-text-subtle)',
          fontWeight: 600,
          margin: '0 0 8px 0',
          fontSize: '13px',
        }}>
          {formatDate(label)}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{
            color: entry.color,
            margin: '4px 0',
            fontSize: '14px',
          }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsAreaChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            {gradient && (
              <defs>
                {keysToPlot.map((key, index) => {
                  const color = config?.colors?.[index] || getSeriesColor(index);
                  return (
                    <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                    </linearGradient>
                  );
                })}
              </defs>
            )}

            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={formatDate}
            />
            <YAxis
              stroke="var(--color-text-faint)"
              tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
              tickLine={false}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            {keysToPlot.length > 1 && <Legend />}

            {keysToPlot.map((key, index) => {
              const color = config?.colors?.[index] || getSeriesColor(index);
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={key.replace(/_/g, ' ')}
                  stroke={color}
                  strokeWidth={2}
                  fill={gradient ? `url(#gradient-${key})` : color}
                  fillOpacity={gradient ? 1 : 0.2}
                  stackId={stacked ? 'stack' : undefined}
                  connectNulls
                />
              );
            })}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default AreaChart;
